import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import rcsLogo from "../../assets/RCS-Logo-Blue.png";
import CheckVoucherPreview from "../../components/accounting/CheckVoucherPreview";
import { API_BASE_URL, authFetch } from "../../config/api";

const todayISO = () => new Date().toISOString().slice(0, 10);

const money = (value) =>
  new Intl.NumberFormat("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const ones = [
  "",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
];

const teens = [
  "Ten",
  "Eleven",
  "Twelve",
  "Thirteen",
  "Fourteen",
  "Fifteen",
  "Sixteen",
  "Seventeen",
  "Eighteen",
  "Nineteen",
];

const tens = [
  "",
  "",
  "Twenty",
  "Thirty",
  "Forty",
  "Fifty",
  "Sixty",
  "Seventy",
  "Eighty",
  "Ninety",
];

const scales = ["", "Thousand", "Million", "Billion", "Trillion"];

const chunkToWords = (n) => {
  const parts = [];
  const hundred = Math.floor(n / 100);
  const rem = n % 100;

  if (hundred) parts.push(`${ones[hundred]} Hundred`);
  if (rem >= 20) {
    parts.push(tens[Math.floor(rem / 10)]);
    if (rem % 10) parts.push(ones[rem % 10]);
  } else if (rem >= 10) {
    parts.push(teens[rem - 10]);
  } else if (rem > 0) {
    parts.push(ones[rem]);
  }

  return parts.join(" ");
};

const numberToWords = (input) => {
  const scaled = Math.round(Number(input || 0) * 100);
  const whole = Math.floor(scaled / 100);
  if (!whole) return "Zero";

  let remaining = whole;
  let scaleIndex = 0;
  const segments = [];

  while (remaining > 0) {
    const chunk = remaining % 1000;
    if (chunk) {
      const words = chunkToWords(chunk);
      segments.unshift(`${words}${scales[scaleIndex] ? ` ${scales[scaleIndex]}` : ""}`);
    }
    remaining = Math.floor(remaining / 1000);
    scaleIndex += 1;
  }

  return segments.join(" ").replace(/\s+/g, " ").trim();
};

const amountToWords = (value) => {
  const scaled = Math.round(Number(value || 0) * 100);
  const whole = Math.floor(scaled / 100);
  const cents = String(scaled % 100).padStart(2, "0");
  return `${numberToWords(whole)} and ${cents}/100 Pesos Only`;
};

const getDisplayName = (user) =>
  [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
  user?.user_name ||
  "";

const resolveSignatureUrl = (value) => {
  const url = String(value || "").trim();
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_BASE_URL.replace(/\/$/, "")}/${url.replace(/^\/+/, "")}`;
};

const getAccountLabel = (account) =>
  `${account?.account_name || ""}${account?.account_type ? ` (${account.account_type})` : ""}`;

const SearchableAccountSelect = ({
  value,
  onChange,
  accounts = [],
  placeholder = "Select account",
  className = "",
}) => {
  const wrapperRef = useRef(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const selectedAccount = useMemo(
    () => accounts.find((account) => String(account.id) === String(value)) || null,
    [accounts, value]
  );

  useEffect(() => {
    setQuery(selectedAccount ? getAccountLabel(selectedAccount) : "");
  }, [selectedAccount]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const filteredAccounts = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return accounts;
    return accounts.filter((account) => {
      const label = getAccountLabel(account).toLowerCase();
      const idText = String(account.id || "").toLowerCase();
      return label.includes(term) || idText.includes(term);
    });
  }, [accounts, query]);

  const selectAccount = (account) => {
    onChange(String(account.id));
    setQuery(getAccountLabel(account));
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          onChange("");
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className={className}
      />
      {open && (
        <div className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {filteredAccounts.length ? (
            filteredAccounts.map((account) => (
              <button
                key={account.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectAccount(account)}
                className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-gray-50"
              >
                <span className="font-medium text-gray-800">{account.account_name}</span>
                <span className="text-xs text-gray-500">
                  {account.account_type ? `${account.account_type}` : `ID ${account.id}`}
                </span>
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500">No accounts found.</div>
          )}
        </div>
      )}
    </div>
  );
};

const SearchableStringSelect = ({
  value,
  onChange,
  options = [],
  placeholder = "Select item",
  emptyLabel = "No items found.",
  className = "",
}) => {
  const wrapperRef = useRef(null);
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const filteredOptions = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) => String(option || "").toLowerCase().includes(term));
  }, [options, query]);

  return (
    <div ref={wrapperRef} className="relative">
      <input
        value={query}
        onChange={(e) => {
          const nextValue = e.target.value;
          setQuery(nextValue);
          setOpen(true);
          onChange(nextValue);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className={className}
      />
      {open && (
        <div className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {filteredOptions.length ? (
            filteredOptions.map((option) => (
              <button
                key={option}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setQuery(option);
                  onChange(option);
                  setOpen(false);
                }}
                className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-gray-50"
              >
                <span className="font-medium text-gray-800">{option}</span>
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500">{emptyLabel}</div>
          )}
        </div>
      )}
    </div>
  );
};

const EWT_OPTIONS = [
  { value: "", label: "No EWT", rate: 0 },
  { value: "ewt_1", label: "EWT 1%", rate: 1 },
  { value: "ewt_2", label: "EWT 2%", rate: 2 },
  { value: "ewt_5", label: "EWT 5%", rate: 5 },
  { value: "ewt_10", label: "EWT 10%", rate: 10 },
  { value: "specific_amount", label: "Specific Amount", rate: 0 },
];

const formatRequestParticulars = (request) =>
  (request?.particulars || [])
    .map((particular) => particular.description || particular.name || "")
    .filter(Boolean)
    .join(", ");

const getEwtLabel = (ewtType, ewtRate) => {
  if (!ewtType) return "";
  if (ewtType === "specific_amount") return "EWT Specific Amount";
  return `EWT ${ewtRate || 0}%`;
};

const isAdvancesToEmployeeAccountName = (value) =>
  /ADVANCES TO OFFICERS?\s*(?:&|AND)\s*EMPLOYEES?/i.test(String(value || ""));

const createAccountTitleRow = (overrides = {}) => ({
  id:
    overrides.id ||
    `account-title-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  account_id: overrides.account_id || "",
  amount: overrides.amount ?? "",
  particulars: overrides.particulars || "",
});

const isMeaningfulAccountTitleRow = (row) =>
  Boolean(
    String(row?.account_id || "").trim() ||
      Number(row?.amount || 0) > 0
  );

const createInputTaxRow = (overrides = {}) => ({
  id:
    overrides.id ||
    `input-tax-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  amount: overrides.amount ?? "",
  other_info: overrides.other_info || "",
});

const isMeaningfulInputTaxRow = (row) => Number(row?.amount || 0) > 0;

const dedupeRequestsById = (items = []) => {
  const seen = new Set();
  return (Array.isArray(items) ? items : []).filter((item) => {
    const key = String(item?.id || "").trim();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getTaxSplit = (grossAmount, taxRate = 12) => {
  const gross = Number(grossAmount || 0);
  if (!gross) return { netAmount: 0, taxAmount: 0 };
  const rate = Number(taxRate || 0);
  if (!rate) return { netAmount: gross, taxAmount: 0 };

  const netAmount = gross / (1 + rate / 100);
  const taxAmount = netAmount * (rate / 100);

  return {
    netAmount: Number(netAmount.toFixed(2)),
    taxAmount: Number(taxAmount.toFixed(2)),
  };
};

const getVatSplit = (grossAmount, taxRate = 12) => {
  const split = getTaxSplit(grossAmount, taxRate);
  return {
    netAmount: split.netAmount,
    inputTaxAmount: split.taxAmount,
  };
};

const formatTaxRateLabel = (rate) => {
  const numericRate = Number(rate || 0);
  if (!numericRate) return "";
  return `${Number.isInteger(numericRate) ? numericRate.toFixed(0) : numericRate.toFixed(2)}%`;
};

const buildGeneratedEntries = ({
  accountTitleRows,
  inputTaxRows,
  accounts,
  selectedTotal,
  withInputTax,
  ewtType,
  ewtRate,
  ewtAmountOverride,
  checkNo,
  cashAccountId,
  particularsTaxAmount = 0,
  particularsTaxLabel = "",
  particularsEwtAmount = 0,
  particularsEwtLabel = "",
}) => {
  const baseAmount = Number(selectedTotal || 0);

  const normalizedInputTaxRows = Array.isArray(inputTaxRows) ? inputTaxRows : [];
  const manualInputTaxRows = normalizedInputTaxRows.filter(isMeaningfulInputTaxRow);
  const accountTitleTotal = Array.isArray(accountTitleRows)
    ? accountTitleRows.reduce((sum, row) => sum + Number(row.amount || 0), 0)
    : 0;
  const ewtBaseAmount = baseAmount;

  // Calculate Input Tax (12% VAT) - deducted from the payable amount
  const inputTaxAmount = withInputTax
    ? getTaxSplit(baseAmount).taxAmount
    : Number(manualInputTaxRows.reduce((sum, row) => sum + Number(row.amount || 0), 0).toFixed(2));

  // Calculate EWT - deducted from payment (credit)
  const computedEwtAmount =
    ewtType && ewtType !== "specific_amount"
      ? Number(((ewtBaseAmount * Number(ewtRate || 0)) / 100).toFixed(2))
      : 0;
  const normalizedOverrideAmount =
    ewtType && ewtAmountOverride !== "" && ewtAmountOverride !== null && ewtAmountOverride !== undefined
      ? Number(Number(ewtAmountOverride || 0).toFixed(2))
      : null;
  const ewtAmount =
    normalizedOverrideAmount !== null && Number.isFinite(normalizedOverrideAmount)
      ? normalizedOverrideAmount
      : computedEwtAmount;

  // Voucher amount stays at the base request total
  const voucherAmount = Number(baseAmount.toFixed(2));

  const normalizedParticularsEwtAmount = Number(particularsEwtAmount || 0);

  // Check amount stays on the base amount side and removes both manual and
  // particulars-derived EWT from cash
  const checkAmount = Number(
    (voucherAmount - ewtAmount - normalizedParticularsEwtAmount).toFixed(2)
  );

  const normalizedAccountTitleRows = Array.isArray(accountTitleRows) ? accountTitleRows : [];
  const cashAccount = accounts.find((account) => String(account.id) === String(cashAccountId));

  const entries = normalizedAccountTitleRows.map((row, index) => {
    const account = accounts.find((item) => String(item.id) === String(row.account_id));
    return {
      id: row.id || `account-title-${index}`,
      role: "account_title",
      account_id: account?.id || row.account_id || "",
      account_name: account?.account_name || "",
      particulars: row.particulars || "",
      amount: row.amount ?? "",
      debit: Number(Number(row.amount || 0).toFixed(2)),
      credit: 0,
      editable: true,
    };
  });

  // Add particulars tax entry (from request items with_tax)
  if (particularsTaxAmount > 0) {
    entries.push({
      id: "particulars_tax",
      role: "particulars_tax",
      account_id: null,
      account_name: "Input Tax (From Particulars)",
      particulars: particularsTaxLabel || "Tax from request particulars",
      debit: Number(particularsTaxAmount),
      credit: 0,
      editable: false,
    });
  }

  // Add particulars EWT entry (from request items ewt_type)
  if (normalizedParticularsEwtAmount > 0) {
    entries.push({
      id: "particulars_ewt",
      role: "particulars_ewt",
      account_id: null,
      account_name: "Withholding Tax Payable",
      particulars: particularsEwtLabel || "EWT from request particulars",
      debit: 0,
      credit: normalizedParticularsEwtAmount,
      editable: false,
    });
  }

  // Add Input Tax entry (VAT 12% or manual rows) as a separate debit line
  if (inputTaxAmount > 0) {
    if (withInputTax) {
      entries.push({
        id: "input_tax_auto",
        role: "input_tax",
        account_id: null,
        account_name: "Input Tax",
        particulars: "VAT 12%",
        debit: inputTaxAmount,
        credit: 0,
        editable: false,
      });
    } else {
      manualInputTaxRows.forEach((row, index) => {
        entries.push({
          id: row.id || `input-tax-${index}`,
          role: "input_tax",
          account_id: null,
          account_name: "Input Tax",
          particulars: row.other_info || "Manual input tax",
          debit: Number(Number(row.amount || 0).toFixed(2)),
          credit: 0,
          editable: false,
        });
      });
    }
  }

  // Add EWT entry (Withholding Tax)
  if (ewtType && ewtAmount > 0) {
    entries.push({
      id: "ewt",
      role: "ewt",
      account_id: null,
      account_name: "Withholding Tax Payable",
      particulars: getEwtLabel(ewtType, ewtRate),
      debit: 0,
      credit: ewtAmount,
      editable: false,
    });
  }

  entries.push({
    id: "cash",
    role: "cash",
    account_id: cashAccount?.id || cashAccountId || "",
    account_name: cashAccount?.account_name || "",
    particulars: checkNo || "Check payment",
    debit: 0,
    credit: checkAmount,
    editable: true,
  });

  return {
    entries,
    accountTitleTotal,
    inputTaxAmount,
    ewtAmount,
    ewtBaseAmount,
    particularsTaxAmount,
    particularsEwtAmount: normalizedParticularsEwtAmount,
    voucherAmount,
    checkAmount
  };
};

const buildPrintHtml = ({
  voucherNo,
  voucherDate,
  payee,
  checkNo,
  checkDate,
  amount,
  amountWords,
  logoUrl,
  requestRowsHtml,
  shipmentConsignee,
  lines,
  preparerName,
  forSoa,
  soaRemarks,
  preparerSignatureUrl,
  checkedByName,
  checkedBySignatureUrl,
  approvedByName,
  receivedByName,
}) => {
  const getLineDisplay = (line) => {
    const role = String(line?.role || "").trim();
    if (role === "account_title") {
      return { account: "—", particulars: "—" };
    }
    if (role === "cash") {
      return { account: "—", particulars: line.particulars || "Check payment" };
    }
    return {
      account: line.account_name || "—",
      particulars: line.particulars || "—",
    };
  };

  const lineRows = lines
    .map((line) => {
      const display = getLineDisplay(line);
      return `<tr>
        <td style="padding:8px 10px;border:1px solid #000;">${display.account}</td>
        <td style="padding:8px 10px;border:1px solid #000;">${display.particulars}</td>
        <td style="padding:8px 10px;border:1px solid #000;text-align:right;">${Number(line.debit || 0) ? money(line.debit) : ""}</td>
        <td style="padding:8px 10px;border:1px solid #000;text-align:right;">${Number(line.credit || 0) ? money(line.credit) : ""}</td>
      </tr>`;
    })
    .join("");
  const entryTotals = lines.reduce(
    (totals, line) => ({
      debit: totals.debit + Number(line.debit || 0),
      credit: totals.credit + Number(line.credit || 0),
    }),
    { debit: 0, credit: 0 }
  );

  return `
    <html>
      <head>
        <title>${voucherNo || "Check Voucher"}</title>
        <style>
          *{box-sizing:border-box;}
          body{font-family:"Times New Roman",serif;color:#000;margin:18px;}
          .brand{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;margin-bottom:8px;}
          .brand img{max-width:180px;max-height:70px;object-fit:contain;}
          h1{text-align:center;letter-spacing:2px;margin:0 0 4px;}
          .subhead{text-align:center;font-weight:600;margin:0 0 12px;}
          .meta{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px 16px;margin-bottom:12px;}
          .meta div{padding:4px 0;}
          table{width:100%;border-collapse:collapse;margin-top:8px;}
          th,td{vertical-align:top;}
          .grid{display:block;}
          .block{padding:0;width:100%;}
          .sig-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-top:16px;}
          .sig{text-align:center;min-height:74px;display:flex;flex-direction:column;justify-content:flex-end;gap:4px;}
          .sig img{max-height:28px;object-fit:contain;margin:0 auto;}
          .sig-label{font-weight:700;}
          .sig-name{font-size:12px;min-height:14px;}
          .line{border-top:1px solid #000;height:1px;margin:6px 0;}
          .small{font-size:12px;}
          .muted{font-size:12px;color:#444;}
          @page{size:portrait;margin:12mm;}
        </style>
      </head>
      <body>
        <div class="brand">
          ${logoUrl ? `<img src="${logoUrl}" alt="RCS Logistics Philippines logo">` : ""}
        </div>
        <h1>CHECK VOUCHER</h1>
        <div class="subhead">${voucherNo || ""}</div>

        <div class="meta">
          <div><strong>Date:</strong> ${voucherDate || ""}</div>
          <div><strong>Payee:</strong> ${payee || ""}</div>
          <div><strong>Check No.:</strong> ${checkNo || ""}</div>
          <div><strong>Check Date:</strong> ${checkDate || ""}</div>
          <div><strong>For SOA:</strong> ${forSoa ? "Yes" : "No"}</div>
          ${forSoa && soaRemarks ? `<div><strong>SOA Remarks:</strong> ${soaRemarks}</div>` : ""}
          ${
            shipmentConsignee
              ? `<div><strong>Shipment Consignee:</strong> ${shipmentConsignee}</div>`
              : ""
          }
          <div><strong>Amount:</strong> ₱${money(amount || 0)}</div>
          <div><strong>Amount in Words:</strong> ${amountWords || ""}</div>
        </div>

        <div style="margin:10px 0 12px;font-size:13px;">
          <div style="font-weight:700;margin-bottom:6px;">The above check is in payment of the following</div>
          <div style="padding-left:12px;line-height:1.5;">
            ${requestRowsHtml || ""}
          </div>
        </div>

        <div class="grid">
          <div class="block">
            <div style="font-weight:700;margin-bottom:6px;">Accounting Entries</div>
            <table>
              <thead>
                <tr>
                  <th style="width:35%;text-align:left;border:1px solid #000;padding:8px 10px;">Account</th>
                  <th style="width:35%;text-align:left;border:1px solid #000;padding:8px 10px;">Particulars</th>
                  <th style="width:15%;text-align:right;border:1px solid #000;padding:8px 10px;">Debit</th>
                  <th style="width:15%;text-align:right;border:1px solid #000;padding:8px 10px;">Credit</th>
                </tr>
              </thead>
              <tbody>
                ${lineRows}
                <tr>
                  <td style="padding:8px 10px;border:1px solid #000;font-weight:700;text-align:right;">TOTAL</td>
                  <td style="padding:8px 10px;border:1px solid #000;"></td>
                  <td style="padding:8px 10px;border:1px solid #000;text-align:right;font-weight:700;">₱${money(entryTotals.debit)}</td>
                  <td style="padding:8px 10px;border:1px solid #000;text-align:right;font-weight:700;">₱${money(entryTotals.credit)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div style="margin-top:14px;font-size:13px;">
          <div><strong>Prepared by:</strong> ${preparerName || "____________________"}</div>
          <div><strong>Checked by:</strong> ${checkedByName || "____________________"}</div>
          <div><strong>Approved by:</strong> ${approvedByName || "____________________"}</div>
          <div><strong>Received by:</strong> ____________________</div>
        </div>

        <div class="sig-grid">
          <div class="sig">
            ${preparerSignatureUrl ? `<img src="${preparerSignatureUrl}" alt="Preparer signature">` : "<div style='height:20px'></div>"}
            <div class="line"></div>
            <div class="sig-name">${preparerName || "Name"}</div>
            <div class="sig-label">Prepared By:</div>
          </div>
          <div class="sig">
            ${checkedBySignatureUrl ? `<img src="${checkedBySignatureUrl}" alt="Checked by signature">` : "<div style='height:20px'></div>"}
            <div class="line"></div>
            <div class="sig-name">${checkedByName || "Name"}</div>
            <div class="sig-label">Checked By:</div>
          </div>
          <div class="sig">
            <div style='height:20px'></div>
            <div class="line"></div>
            <div class="sig-name">${approvedByName || "Name"}</div>
            <div class="sig-label">Approved By:</div>
          </div>
          <div class="sig">
            <div style='height:20px'></div>
            <div class="line"></div>
            <div class="sig-name">${receivedByName || "Name"}</div>
            <div class="sig-label">Received By:</div>
          </div>
        </div>
      </body>
    </html>
  `;
};

const CheckVoucherCreate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useSelector((state) => state.auth.user);
  const editVoucherId = searchParams.get("edit") || "";
  const isEditMode = Boolean(editVoucherId);

  const [requests, setRequests] = useState([]);
  const [requestShipments, setRequestShipments] = useState({});
  const [checkedByUsers, setCheckedByUsers] = useState([]);
  const [checkedByUserId, setCheckedByUserId] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [voucherNo, setVoucherNo] = useState("");
  const [voucherDate, setVoucherDate] = useState(todayISO());
  const [checkNo, setCheckNo] = useState("");
  const [checkDate, setCheckDate] = useState(todayISO());
  const [withInputTax, setWithInputTax] = useState(false);
  const [forSoa, setForSoa] = useState(false);
  const [soaRemarks, setSoaRemarks] = useState("");
  const [soaFile, setSoaFile] = useState(null);
  const [existingSoaFile, setExistingSoaFile] = useState(null);
  const [inputTaxRows, setInputTaxRows] = useState([]);
  const [inputTaxPurposes, setInputTaxPurposes] = useState([]);
  const [ewtType, setEwtType] = useState("");
  const [ewtAmountInput, setEwtAmountInput] = useState("");
  const [preparerName, setPreparerName] = useState("");
  const [preparerSignatureUrl, setPreparerSignatureUrl] = useState("");
  const [accountTitleRows, setAccountTitleRows] = useState([]);
  const [cashAccountId, setCashAccountId] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedVoucher, setSavedVoucher] = useState(null);
  const [saveSuccessOpen, setSaveSuccessOpen] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [editingVoucherData, setEditingVoucherData] = useState(null);
  const saveInFlightRef = useRef(false);
  const previousDefaultAccountTitleAmountRef = useRef(0);

  const currentUserName =
    [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
    user?.user_name ||
    "";
  const approvedByName = "EDR/ERI/AGR";
  const pageTitle = isEditMode ? "Edit Check Voucher" : "Check Voucher Creation";
  const pageDescription = isEditMode
    ? "Edit the voucher using the same accounting form."
    : "Build and preview the check voucher before downloading it.";
  const primaryActionLabel = isEditMode ? "Update Voucher" : "Save Voucher";
  const successTitle = isEditMode ? "Voucher updated" : "Voucher saved";
  const successMessage = isEditMode ? "has been updated successfully." : "has been created successfully.";
  const currentCheckedByUser = useMemo(
    () => checkedByUsers.find((u) => String(u.user_id) === String(checkedByUserId)),
    [checkedByUsers, checkedByUserId]
  );
  const checkedByDisplayName = currentCheckedByUser ? getDisplayName(currentCheckedByUser) : "";

  const selectedTotal = useMemo(
    () => requests.reduce((sum, r) => sum + Number(r.total_amount || 0), 0),
    [requests]
  );
  // Calculate total tax from request particulars that have with_tax enabled
  const particularsTaxTotal = useMemo(() => {
    return requests.reduce((sum, request) => {
      const particulars = request.particulars || [];
      const requestTax = particulars.reduce((pSum, p) => {
        if (p.with_tax) {
          return pSum + Number(p.tax_amount || 0);
        }
        return pSum;
      }, 0);
      return sum + requestTax;
    }, 0);
  }, [requests]);
  const particularsTaxLabel = useMemo(() => {
    const uniqueRates = Array.from(
      new Set(
        requests.flatMap((request) =>
          (request.particulars || [])
            .filter((p) => Boolean(p.with_tax))
            .map((p) => Number(p.tax_rate || 12))
            .filter((rate) => rate > 0)
        )
      )
    ).sort((a, b) => a - b);

    if (!uniqueRates.length) return "";
    if (uniqueRates.length === 1) {
      return `Tax ${formatTaxRateLabel(uniqueRates[0])} from request particulars`;
    }

    return `Tax (${uniqueRates.map(formatTaxRateLabel).join(", ")}) from request particulars`;
  }, [requests]);
  const hasIncludedInputTax = particularsTaxTotal > 0;

  // Calculate total EWT from request particulars that have ewt_type set
  const particularsEwtTotal = useMemo(() => {
    return requests.reduce((sum, request) => {
      const particulars = request.particulars || [];
      const requestEwt = particulars.reduce((pSum, p) => {
        if (p.ewt_type) {
          return pSum + Number(p.ewt_amount || 0);
        }
        return pSum;
      }, 0);
      return sum + requestEwt;
    }, 0);
  }, [requests]);
  const particularsEwtLabel = useMemo(() => {
    const uniqueTypes = Array.from(
      new Set(
        requests.flatMap((request) =>
          (request.particulars || [])
            .filter((p) => Boolean(p.ewt_type))
            .map((p) => getEwtLabel(p.ewt_type, p.ewt_rate))
            .filter(Boolean)
        )
      )
    );

    if (!uniqueTypes.length) return "";
    if (uniqueTypes.length === 1) {
      return `${uniqueTypes[0]} from request particulars`;
    }

    return `EWT (${uniqueTypes.join(", ")}) from request particulars`;
  }, [requests]);
  const hasIncludedEwt = particularsEwtTotal > 0;

  const selectedPayees = useMemo(
    () => Array.from(new Set(requests.map((r) => (r.payee || "").trim()).filter(Boolean))),
    [requests]
  );
  const selectedPayee = selectedPayees.join(", ");
  const selectedCheckNos = useMemo(
    () => Array.from(new Set(requests.map((r) => (r.check_no || "").trim()).filter(Boolean))),
    [requests]
  );

  const selectedEwtOption = EWT_OPTIONS.find((option) => option.value === ewtType) || EWT_OPTIONS[0];
  const computedEwtAmount = useMemo(
    () =>
      ewtType && ewtType !== "specific_amount"
        ? Number(((Number(selectedTotal || 0) * Number(selectedEwtOption.rate || 0)) / 100).toFixed(2))
        : 0,
    [ewtType, selectedEwtOption.rate, selectedTotal]
  );
  const seededInputTaxAmount = useMemo(() => {
    if (withInputTax) {
      return getVatSplit(selectedTotal).inputTaxAmount;
    }

    return Number(
      inputTaxRows
        .filter(isMeaningfulInputTaxRow)
        .reduce((sum, row) => sum + Number(row.amount || 0), 0)
        .toFixed(2)
    );
  }, [inputTaxRows, selectedTotal, withInputTax]);

  const defaultAccountTitleAmount = Number(
    Math.max(Number(selectedTotal || 0) - Number(particularsTaxTotal || 0) - Number(seededInputTaxAmount || 0), 0).toFixed(2)
  );

  const generated = useMemo(
    () =>
      buildGeneratedEntries({
        accountTitleRows,
        inputTaxRows,
        accounts,
        selectedTotal,
        withInputTax,
        ewtType,
        ewtRate: selectedEwtOption.rate,
        ewtAmountOverride: ewtAmountInput,
        checkNo,
        cashAccountId,
        particularsTaxAmount: particularsTaxTotal,
        particularsTaxLabel,
        particularsEwtAmount: particularsEwtTotal,
        particularsEwtLabel,
      }),
    [
      accountTitleRows,
      inputTaxRows,
      accounts,
      cashAccountId,
      checkNo,
      particularsTaxTotal,
      particularsTaxLabel,
      particularsEwtTotal,
      particularsEwtLabel,
      selectedEwtOption.rate,
      selectedTotal,
      withInputTax,
      ewtAmountInput,
      ewtType,
    ]
  );

  const inputTaxAmount = generated.inputTaxAmount;
  const ewtAmount = generated.ewtAmount;
  const ewtBaseAmount = generated.ewtBaseAmount;
  const voucherAmount = generated.voucherAmount;
  const checkAmount = generated.checkAmount;
  const printableAmount = ewtAmount > 0 || generated.particularsEwtAmount > 0 ? checkAmount : voucherAmount;
  const amountWords = amountToWords(printableAmount);

  const requestRows = useMemo(
    () =>
      requests.map((request) => {
        const shipment = requestShipments[String(request.job_order || "").trim()] || null;
        return {
          job_order: request.job_order || "—",
          rfp_no: request.rfp_no || "—",
          particulars: formatRequestParticulars(request) || "—",
          client: shipment?.corporate_client || "—",
          shipment_consignee: shipment?.receiver_name || request?.shipment_consignee || request?.receiver_name || "—",
        };
      }),
    [requestShipments, requests]
  );

  useEffect(() => {
    if (isEditMode) {
      const loadVoucherForEdit = async () => {
        setLoading(true);
        setError("");
        try {
          const res = await authFetch(`${API_BASE_URL}/api/accounting/check-vouchers/${editVoucherId}`, {
            credentials: "include",
          });
          const json = await res.json();
          if (!json.success) throw new Error(json.message || "Unable to load voucher");

          const voucher = json.data || null;
          if (!voucher) throw new Error("Voucher not found");

          const voucherRequests = dedupeRequestsById(Array.isArray(voucher.requests) ? voucher.requests : []);
          const voucherLines = Array.isArray(voucher.lines) ? voucher.lines : [];
          const accountTitleLines = voucherLines
            .filter((line) => String(line.line_type || "") === "account_title")
            .map((line) =>
              createAccountTitleRow({
                id: line.id,
                account_id: line.account_id || "",
                amount: line.debit ?? line.amount ?? "",
                particulars: line.particulars || "",
              })
            );
          const manualInputTaxLines = voucherLines
            .filter((line) => String(line.line_type || "") === "input_tax")
            .map((line) =>
              createInputTaxRow({
                id: line.id,
                amount: line.debit ?? line.amount ?? "",
                other_info: line.particulars || "",
              })
            );
          const cashLine = voucherLines.find((line) => String(line.line_type || "") === "cash");

          setEditingVoucherData(voucher);
          setRequests(voucherRequests);
          setVoucherNo(voucher.voucher_no || "");
          setVoucherDate(String(voucher.voucher_date || todayISO()).slice(0, 10));
          setCheckNo(voucher.check_no || "");
          setCheckDate(String(voucher.check_date || todayISO()).slice(0, 10));
          setWithInputTax(Boolean(voucher.with_input_tax));
          setInputTaxRows(
            voucher.with_input_tax
              ? [createInputTaxRow()]
              : manualInputTaxLines.length
                ? manualInputTaxLines
                : [createInputTaxRow()]
          );
          const savedVoucherEwtType =
            typeof voucher.tax_type === "string" && voucher.tax_type.startsWith("ewt_")
              ? voucher.tax_type
              : "";
          setEwtType(savedVoucherEwtType);
          setEwtAmountInput(
            savedVoucherEwtType &&
              voucher.tax_amount !== null &&
              voucher.tax_amount !== undefined
              ? String(voucher.tax_amount)
              : ""
          );
          setPreparerName(voucher.preparer_name || "");
          setPreparerSignatureUrl(voucher.preparer_signature_url || "");
          setForSoa(Boolean(voucher.for_soa));
          setSoaRemarks(voucher.soa_remarks || "");
          setExistingSoaFile(voucher.soa_file_name ? {
            name: voucher.soa_file_name,
            url: voucher.soa_file_url || "",
          } : null);
          setSoaFile(null);
          setAccountTitleRows(accountTitleLines.length ? accountTitleLines : [createAccountTitleRow()]);
          setCashAccountId(String(cashLine?.account_id || ""));
          setCheckedByUserId(String(voucher.assigned_checker_user_id || ""));
          setSavedVoucher(null);
          setSaveSuccessOpen(false);
        } catch (err) {
          setError(err.message || "Unable to load voucher");
        } finally {
          setLoading(false);
        }
      };

      loadVoucherForEdit();
      return;
    }

    const draft =
      location.state?.requests?.length
        ? location.state
        : (() => {
            try {
              return JSON.parse(sessionStorage.getItem("checkVoucherDraft") || "null");
            } catch {
              return null;
            }
          })();

    if (!draft?.requests?.length) {
      setLoading(false);
      return;
    }

    setRequests(dedupeRequestsById(draft.requests));
    if (draft.assigned_checker_user_id) {
      setCheckedByUserId(String(draft.assigned_checker_user_id));
    }
    setEwtAmountInput("");
    setLoading(false);
  }, [editVoucherId, isEditMode, location.state]);

  useEffect(() => {
    if (!requests.length) {
      return;
    }
    setAccountTitleRows((prev) =>
      prev.length ? prev : [createAccountTitleRow({ amount: defaultAccountTitleAmount })]
    );
  }, [defaultAccountTitleAmount, requests.length]);

  useEffect(() => {
    if (isEditMode || !requests.length || !accountTitleRows.length) {
      previousDefaultAccountTitleAmountRef.current = defaultAccountTitleAmount;
      return;
    }

    const grossSelectedTotal = Number(selectedTotal || 0);
    const previousDefaultAmount = Number(previousDefaultAccountTitleAmountRef.current || 0);

    setAccountTitleRows((prev) => {
      if (!prev.length) return prev;

      const firstRow = prev[0];
      const firstAmount = Number(firstRow.amount || 0);
      const looksAutoFilledFromGross =
        !String(firstRow.amount || "").trim() ||
        Math.abs(firstAmount - grossSelectedTotal) < 0.01;
      const looksAutoFilledFromPreviousDefault =
        previousDefaultAmount > 0 && Math.abs(firstAmount - previousDefaultAmount) < 0.01;

      if (!looksAutoFilledFromGross && !looksAutoFilledFromPreviousDefault) {
        return prev;
      }

      if (Math.abs(firstAmount - defaultAccountTitleAmount) < 0.01) {
        return prev;
      }

      const next = [...prev];
      next[0] = {
        ...firstRow,
        amount: defaultAccountTitleAmount,
      };
      return next;
    });

    previousDefaultAccountTitleAmountRef.current = defaultAccountTitleAmount;
  }, [accountTitleRows.length, defaultAccountTitleAmount, isEditMode, requests.length, selectedTotal]);

  useEffect(() => {
    if (isEditMode || !requests.length) {
      return;
    }

    if (hasIncludedInputTax && withInputTax) {
      setWithInputTax(false);
    }

    if (!withInputTax) {
      setInputTaxRows((prev) => (prev.length ? prev : [createInputTaxRow()]));
    }
  }, [hasIncludedInputTax, isEditMode, requests.length, withInputTax]);

  useEffect(() => {
    if (isEditMode || !requests.length) {
      return;
    }

    if (hasIncludedEwt && ewtType) {
      setEwtType("");
      setEwtAmountInput("");
    }
  }, [hasIncludedEwt, isEditMode, requests.length, ewtType]);

  useEffect(() => {
    if (isEditMode) {
      return;
    }

    setPreparerName(currentUserName);
    const signatureUrl = resolveSignatureUrl(user?.signature_url);

    if (signatureUrl) {
      setPreparerSignatureUrl(signatureUrl);
      return;
    }

    let cancelled = false;
    const loadSignature = async () => {
      try {
        const res = await authFetch(`${API_BASE_URL}/api/auth/check-auth`, {
          credentials: "include",
          headers: {
            "Cache-Control": "no-store, no-cache, must-revalidate, proxy-revalidate",
            Pragma: "no-cache",
            Expires: "0",
          },
        });
        const json = await res.json();
        const fetchedSignature = json.success ? resolveSignatureUrl(json.user?.signature_url) : "";
        if (!cancelled) {
          setPreparerSignatureUrl(fetchedSignature);
        }
      } catch (err) {
        console.error("load preparer signature", err);
      }
    };

    loadSignature();
    return () => {
      cancelled = true;
    };
  }, [currentUserName, isEditMode, user?.signature_url]);

  useEffect(() => {
    const loadCheckedByUsers = async () => {
      try {
        const res = await authFetch(`${API_BASE_URL}/api/accounting/check-vouchers/checked-by-users`, {
          credentials: "include",
        });
        const json = await res.json();
        if (json.success) {
          const users = json.data || [];
          setCheckedByUsers(users);
          if (users.length === 1) {
            setCheckedByUserId((current) => current || String(users[0].user_id));
          }
        }
      } catch (err) {
        console.error("load checked-by users", err);
      }
    };

    loadCheckedByUsers();
  }, []);

  useEffect(() => {
    const loadShipmentDetails = async () => {
      const jobOrders = Array.from(
        new Set(requests.map((request) => String(request.job_order || "").trim()).filter(Boolean))
      );

      if (!jobOrders.length) {
        setRequestShipments({});
        return;
      }

      try {
        const pairs = await Promise.all(
          jobOrders.map(async (jobOrder) => {
            const url = new URL(`${API_BASE_URL}/api/accounting/shipments`);
            url.searchParams.set("q", jobOrder);
            url.searchParams.set("limit", 1);
            const res = await authFetch(url, { credentials: "include" });
            const json = await res.json();
            const shipment = json.success && json.data?.length ? json.data[0] : null;
            return [jobOrder, shipment];
          })
        );

        setRequestShipments(Object.fromEntries(pairs));
      } catch (err) {
        console.error("load shipment details", err);
        setRequestShipments({});
      }
    };

    loadShipmentDetails();
  }, [requests]);

  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const res = await authFetch(`${API_BASE_URL}/api/accounting/check-vouchers/accounts`, {
          credentials: "include",
        });
        const json = await res.json();
        if (json.success) setAccounts((json.data || []).filter((a) => a.status !== "inactive"));
      } catch (err) {
        console.error("load accounts", err);
      }
    };
    loadAccounts();
  }, []);

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const res = await authFetch(`${API_BASE_URL}/api/accounting/check-vouchers/employees`, {
          credentials: "include",
        });
        const json = await res.json();
        if (json.success) {
          setEmployees(Array.isArray(json.data) ? json.data : []);
        }
      } catch (err) {
        console.error("load employees", err);
      }
    };

    loadEmployees();
  }, []);

  useEffect(() => {
    const loadInputTaxPurposes = async () => {
      try {
        const res = await authFetch(`${API_BASE_URL}/api/accounting/check-vouchers/input-tax-purposes`, {
          credentials: "include",
        });
        const json = await res.json();
        if (json.success) {
          setInputTaxPurposes(Array.isArray(json.data) ? json.data : []);
        }
      } catch (err) {
        console.error("load input tax purposes", err);
      }
    };

    loadInputTaxPurposes();
  }, []);

  useEffect(() => {
    if (isEditMode) return;

    const loadVoucherNo = async () => {
      try {
        const url = new URL(`${API_BASE_URL}/api/accounting/check-vouchers/next-number`);
        url.searchParams.set("date", voucherDate);
        const res = await authFetch(url, { credentials: "include" });
        const json = await res.json();
        if (json.success) setVoucherNo(json.data?.voucher_no || "");
      } catch (err) {
        console.error("load voucher no", err);
      }
    };
    loadVoucherNo();
  }, [isEditMode, voucherDate]);

  useEffect(() => {
    if (!requests.length) return;
  }, [requests.length, hasIncludedInputTax, withInputTax]);

  useEffect(() => {
    if (isEditMode) return;

    if (requests.length > 0) {
      const autoCheckNo = selectedCheckNos.length === 1 ? selectedCheckNos[0] : "";
      if (autoCheckNo) setCheckNo(autoCheckNo);
    }
  }, [isEditMode, requests, selectedCheckNos]);

  useEffect(() => {
    if (!ewtType) {
      setEwtAmountInput("");
      return;
    }

    if (ewtType === "specific_amount") {
      return;
    }

    if (isEditMode) {
      return;
    }

    setEwtAmountInput(String(computedEwtAmount || 0));
  }, [computedEwtAmount, ewtAmountInput, ewtType, isEditMode]);

  const updateAccountTitleRow = (index, field, value) => {
    setAccountTitleRows((prev) =>
      prev.map((row, idx) => (idx === index ? { ...row, [field]: value } : row))
    );
  };

  const updateAccountTitleAccount = (index, accountId) => {
    const account = accounts.find((item) => String(item.id) === String(accountId));
    const shouldUseEmployee = isAdvancesToEmployeeAccountName(account?.account_name);
    setAccountTitleRows((prev) =>
      prev.map((row, idx) =>
        idx === index
          ? {
              ...row,
              account_id: accountId,
              particulars: shouldUseEmployee ? row.particulars || "" : "",
            }
          : row
      )
    );
  };

  const addAccountTitleRow = () => {
    setAccountTitleRows((prev) => [...prev, createAccountTitleRow({ amount: defaultAccountTitleAmount })]);
  };

  const removeAccountTitleRow = (index) => {
    setAccountTitleRows((prev) => prev.filter((_, idx) => idx !== index));
  };

  const updateInputTaxRow = (index, field, value) => {
    setInputTaxRows((prev) =>
      prev.map((row, idx) => (idx === index ? { ...row, [field]: value } : row))
    );
  };

  const addInputTaxRow = () => {
    setInputTaxRows((prev) => [...prev, createInputTaxRow()]);
  };

  const removeInputTaxRow = (index) => {
    setInputTaxRows((prev) => prev.filter((_, idx) => idx !== index));
  };

  const lineTotals = useMemo(
    () => ({
      debit: generated.entries.reduce((sum, line) => sum + Number(line.debit || 0), 0),
      credit: generated.entries.reduce((sum, line) => sum + Number(line.credit || 0), 0),
    }),
    [generated.entries]
  );

  const expectedAccountTitleTotal = Number(
    Math.max(Number(selectedTotal || 0) - Number(particularsTaxTotal || 0) - Number(inputTaxAmount || 0), 0).toFixed(2)
  );
  const accountTitleRowsForSave = useMemo(
    () => accountTitleRows.filter(isMeaningfulAccountTitleRow),
    [accountTitleRows]
  );
  const inputTaxRowsForSave = useMemo(
    () => inputTaxRows.filter(isMeaningfulInputTaxRow),
    [inputTaxRows]
  );
  const accountTitleTotal = Number((generated.accountTitleTotal || 0).toFixed(2));
  const accountTitleBalanceDiff = Number((accountTitleTotal - expectedAccountTitleTotal).toFixed(2));
  const hasValidAccountTitleRows =
    accountTitleRowsForSave.length > 0 &&
    accountTitleRowsForSave.every(
      (row) => String(row.account_id || "").trim() && Number(row.amount || 0) > 0
    );
  const hasValidManualInputTaxRows =
    withInputTax ||
    inputTaxRowsForSave.length === 0 ||
    inputTaxRowsForSave.every((row) => Number(row.amount || 0) > 0);
  const manualInputTaxTotal = Number(
    inputTaxRowsForSave.reduce((sum, row) => sum + Number(row.amount || 0), 0).toFixed(2)
  );

  const taxRoles = ["input_tax", "ewt", "particulars_tax", "particulars_ewt"];
  const totalEwtAmount = Number((ewtAmount + generated.particularsEwtAmount).toFixed(2));
  const canSave =
    !!voucherNo &&
    !!selectedPayees.length &&
    voucherAmount > 0 &&
    ewtAmount >= 0 &&
    totalEwtAmount <= voucherAmount &&
    checkAmount >= 0 &&
    Math.abs(lineTotals.debit - lineTotals.credit) < 0.01 &&
    Math.abs(lineTotals.debit - voucherAmount) < 0.01 &&
    generated.entries.length > 0 &&
    hasValidAccountTitleRows &&
    String(cashAccountId || "").trim() &&
    Math.abs(accountTitleBalanceDiff) < 0.01 &&
    hasValidManualInputTaxRows &&
    (!withInputTax ? manualInputTaxTotal >= 0 : true) &&
    generated.entries
      .filter((line) => !taxRoles.includes(line.role) && line.role !== "account_title")
      .every((line) => line.account_id) &&
    !saving;

  const buildPayload = () => ({
    voucher_date: voucherDate,
    payee: selectedPayee,
    check_no: checkNo || null,
    check_date: checkDate || null,
    with_input_tax: withInputTax,
    input_tax_amount: inputTaxAmount || null,
    tax_type: withInputTax ? "input_tax" : null,  // Add tax_type for Input Tax
    tax_amount: inputTaxAmount || null,  // Add tax_amount for Input Tax
    ewt_type: ewtType || null,
    ewt_rate: selectedEwtOption.rate || null,
    ewt_amount: ewtAmount || null,
    particulars_tax_amount: particularsTaxTotal || null,
    particulars_ewt_amount: particularsEwtTotal || null,
    amount: voucherAmount,
    amount_words: amountWords,
    request_ids: Array.from(new Set(requests.map((r) => String(r.id)).filter(Boolean))),
    preparer_name: preparerName || null,
    preparer_signature_url: preparerSignatureUrl || null,
    for_soa: forSoa,
    soa_remarks: forSoa ? soaRemarks.trim() || null : null,
    approved_by_name: approvedByName || null,
    assigned_checker_user_id: checkedByUserId || null,
    assigned_checker_name: checkedByDisplayName || null,
    checked_by_user_id: isEditMode ? editingVoucherData?.checked_by_user_id ?? null : null,
    checked_by_name: isEditMode ? editingVoucherData?.checked_by_name ?? null : null,
    checked_by_signature_url: isEditMode ? editingVoucherData?.checked_by_signature_url ?? null : null,
    approved_by_signature_url: isEditMode
      ? editingVoucherData?.approved_by_signature_url ?? null
      : null,
    lines: generated.entries.map((line, index) => {
      const account = accounts.find((a) => a.id === line.account_id);
      return {
        line_order: index + 1,
        line_type: line.role || null,
        account_id: line.account_id || null,
        account_name: account?.account_name || line.account_name || "",
        particulars: line.particulars || null,
        debit: Number(line.debit || 0),
        credit: Number(line.credit || 0),
      };
    }),
  });

  const handleSave = async () => {
    if (saveInFlightRef.current || saving) {
      return;
    }
    if (!selectedPayees.length) {
      setError("At least one payee is required.");
      return;
    }
    if (!checkNo.trim()) {
      setError("Check number is required.");
      return;
    }
    if (ewtAmount < 0) {
      setError("EWT amount cannot be negative.");
      return;
    }
    if (totalEwtAmount > voucherAmount) {
      setError("EWT amount cannot be greater than the voucher amount.");
      return;
    }
    if (Math.abs(lineTotals.debit - lineTotals.credit) > 0.01) {
      setError("Debit and credit must balance.");
      return;
    }
    if (Math.abs(lineTotals.debit - voucherAmount) > 0.01) {
      setError("Debit and credit total must match the voucher amount.");
      return;
    }
    if (!hasValidAccountTitleRows) {
      setError("Add at least one account title row with an account and amount.");
      return;
    }
    if (Math.abs(accountTitleBalanceDiff) > 0.01) {
      setError("Account title amounts must match the request total.");
      return;
    }
    if (!String(cashAccountId || "").trim()) {
      setError("Select a cash/bank account.");
      return;
    }
    if (forSoa && !soaFile && !existingSoaFile) {
      setError("Upload the SOA file when For SOA is selected.");
      return;
    }
    if (generated.entries.filter((line) => !taxRoles.includes(line.role) && line.role !== "account_title").some((line) => !line.account_id)) {
      setError("Select a chart of account for the cash/bank line.");
      return;
    }

    setSaving(true);
    saveInFlightRef.current = true;
    setError("");
    try {
      const res = await authFetch(
        isEditMode
          ? `${API_BASE_URL}/api/accounting/check-vouchers/${editingVoucherData?.id || editVoucherId}`
          : `${API_BASE_URL}/api/accounting/check-vouchers`,
        {
          method: isEditMode ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(buildPayload()),
        }
      );
      const json = await res.json();
      if (!json.success) throw new Error(json.message || "Voucher save failed");
      let savedData = json.data;
      if (soaFile && savedData?.id) {
        const formData = new FormData();
        formData.append("soa_file", soaFile);
        const soaResponse = await authFetch(
          `${API_BASE_URL}/api/accounting/check-vouchers/${savedData.id}/soa`,
          { method: "POST", credentials: "include", body: formData }
        );
        const soaJson = await soaResponse.json();
        if (!soaResponse.ok || !soaJson.success) {
          throw new Error(soaJson.message || "SOA upload failed");
        }
        savedData = soaJson.data;
        setExistingSoaFile({ name: savedData.soa_file_name, url: savedData.soa_file_url || "" });
        setSoaFile(null);
      }
      setSavedVoucher(savedData);
      setSaveSuccessOpen(true);
      sessionStorage.removeItem("checkVoucherDraft");
    } catch (err) {
      setError(err.message || "Voucher save failed");
    } finally {
      saveInFlightRef.current = false;
      setSaving(false);
    }
  };

  const printVoucher = () => {
    const voucher = savedVoucher || {
      voucher_no: voucherNo,
      voucher_date: voucherDate,
      payee: selectedPayee,
      check_no: checkNo,
      check_date: checkDate,
      with_input_tax: withInputTax,
      input_tax_amount: inputTaxAmount,
      ewt_type: ewtType,
      ewt_rate: selectedEwtOption.rate,
      ewt_amount: ewtAmount,
      amount: printableAmount,
      amount_words: amountWords,
      lines: buildPayload().lines,
      preparer_name: preparerName,
      preparer_signature_url: preparerSignatureUrl,
      for_soa: forSoa,
      soa_remarks: soaRemarks,
      checked_by_name: savedVoucher?.checked_by_name || checkedByDisplayName,
      checked_by_signature_url: savedVoucher?.checked_by_signature_url || null,
      approved_by_name: approvedByName,
      requests,
    };
    const printableVoucher = {
      ...voucher,
      amount: printableAmount,
      amount_words: amountWords,
    };

    const win = window.open("", "_blank");
    if (!win) return;
    win.document.open();
    win.document.write(
      buildPrintHtml({
        voucherNo: printableVoucher.voucher_no,
        voucherDate: printableVoucher.voucher_date,
        payee: printableVoucher.payee,
        checkNo: printableVoucher.check_no || "",
        checkDate: printableVoucher.check_date || "",
        amount: printableVoucher.amount,
        amountWords: printableVoucher.amount_words,
        logoUrl: rcsLogo,
        shipmentConsignee: Array.from(
          new Set(
            requestRows
              .map((row) => String(row.shipment_consignee || "").trim())
              .filter((value) => value && value !== "—")
          )
        ).join(", "),
        requestRowsHtml: requestRows
          .map(
            (row) => `<div style="margin-bottom:4px;">
              <strong>JO:</strong> ${row.job_order || "—"}
              &nbsp;&nbsp;<strong>RFP No:</strong> ${row.rfp_no || "—"}
              &nbsp;&nbsp;<strong>Particulars:</strong> ${row.particulars || "—"}
              &nbsp;&nbsp;<strong>Client:</strong> ${row.client || "—"}
            </div>`
          )
          .join(""),
        lines: printableVoucher.lines || buildPayload().lines,
        preparerName: printableVoucher.preparer_name,
        preparerSignatureUrl: printableVoucher.preparer_signature_url,
        forSoa: Boolean(printableVoucher.for_soa),
        soaRemarks: printableVoucher.soa_remarks || "",
        checkedByName: printableVoucher.checked_by_name || printableVoucher.assigned_checker_name || checkedByDisplayName,
        checkedBySignatureUrl: printableVoucher.checked_by_signature_url || null,
        approvedByName: printableVoucher.approved_by_name,
        receivedByName: printableVoucher.received_by_name || "Pending",
      })
    );
    win.document.close();
    win.focus();
    win.print();
  };

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">Loading voucher draft...</div>;
  }

  if (!requests.length) {
    return (
      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6">
        <h1 className="text-2xl font-semibold text-blue">{pageTitle}</h1>
        <p className="mt-2 text-sm text-gray-600">
          No selected requests were passed to this page.
        </p>
        <button
          onClick={() => navigate(isEditMode ? "/accounting/check-vouchers" : "/accounting/requests")}
          className="mt-4 px-4 py-2 rounded-lg bg-blue text-white text-sm font-semibold"
        >
          {isEditMode ? "Back to Vouchers" : "Back to Requests"}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 font-poppins">
      {saving && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full border-4 border-blue/20 border-t-blue animate-spin" />
              <div>
                <p className="text-lg font-semibold text-gray-900">Saving voucher</p>
                <p className="text-sm text-gray-500">Please wait while the voucher is being created.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {saveSuccessOpen && savedVoucher && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="save-voucher-success-title"
          onClick={() => setSaveSuccessOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-700">
                <i className="fas fa-check text-xl" aria-hidden="true" />
              </div>
              <div className="flex-1">
                <h2 id="save-voucher-success-title" className="text-xl font-semibold text-gray-900">
                  {successTitle}
                </h2>
                <p className="mt-1 text-sm text-gray-600">
                  {savedVoucher?.voucher_no || voucherNo} {successMessage}
                </p>
              </div>
            </div>

            <div className="mt-5 rounded-2xl bg-gray-50 p-4 text-sm text-gray-700">
              The voucher is now ready for checking and printing.
            </div>

            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setSaveSuccessOpen(false)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  setSaveSuccessOpen(false);
                  printVoucher();
                }}
                className="rounded-lg bg-blue px-4 py-2 text-sm font-semibold text-white hover:bg-blue/90"
              >
                Download / Print
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white/95 border border-gray-100 rounded-3xl shadow-sm p-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.14em] text-gray-500">Accounting</p>
          <h1 className="text-2xl font-semibold text-blue">{pageTitle}</h1>
          <p className="text-sm text-gray-600">{pageDescription}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => navigate(isEditMode ? "/accounting/check-vouchers" : "/accounting/requests")}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
          >
            {isEditMode ? "Back to Vouchers" : "Back to Requests"}
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className="px-4 py-2 rounded-lg bg-blue text-white text-sm font-semibold hover:bg-blue/90 disabled:opacity-50"
          >
            {saving ? "Saving..." : primaryActionLabel}
          </button>
          <button
            onClick={printVoucher}
            disabled={!savedVoucher}
            className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            title={!savedVoucher ? "Save the voucher first" : "Download / Print"}
          >
            Download / Print
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1.05fr_0.95fr] gap-6">
        <div className="space-y-4">
          <div className="bg-white/95 border border-gray-100 rounded-3xl shadow-sm p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-semibold text-gray-700">Check Voucher No.</label>
                <input
                  value={voucherNo}
                  readOnly
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 bg-gray-50 text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">Date</label>
                <input
                  type="date"
                  value={voucherDate}
                  onChange={(e) => setVoucherDate(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">Payee</label>
                <input
                  value={selectedPayee}
                  readOnly
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 bg-gray-50 text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">Check No.</label>
                <input
                  value={checkNo}
                  onChange={(e) => setCheckNo(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  placeholder="Enter check number"
                  readOnly={selectedCheckNos.length === 1}
                />
                {selectedCheckNos.length === 1 && (
                  <p className="mt-1 text-xs text-gray-500">
                    Auto-filled from the selected request.
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">Check Date</label>
                <input
                  type="date"
                  value={checkDate}
                  onChange={(e) => setCheckDate(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                />
              </div>
              <div className="md:col-span-2 rounded-xl border border-blue-100 bg-blue-50/50 px-3 py-3">
                <label className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <input
                    type="checkbox"
                    checked={forSoa}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setForSoa(checked);
                      if (!checked) setSoaRemarks("");
                    }}
                    className="h-4 w-4"
                  />
                  For SOA
                </label>
                {forSoa && (
                  <div className="mt-3">
                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                      SOA Remarks
                    </label>
                    <textarea
                      value={soaRemarks}
                      onChange={(e) => setSoaRemarks(e.target.value)}
                      rows={2}
                      className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      placeholder="Enter remarks for the SOA"
                    />
                    <label className="mt-3 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                      SOA Attachment
                    </label>
                    <input
                      type="file"
                      accept="application/pdf,image/png,image/jpeg,image/webp"
                      onChange={(e) => setSoaFile(e.target.files?.[0] || null)}
                      className="mt-1 block w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      PDF or image, maximum 15 MB.
                    </p>
                    {(soaFile || existingSoaFile) && (
                      <p className="mt-1 text-xs font-medium text-green-700">
                        Selected: {soaFile?.name || existingSoaFile?.name}
                        {existingSoaFile?.url && !soaFile && (
                          <a
                            href={existingSoaFile.url}
                            target="_blank"
                            rel="noreferrer"
                            className="ml-2 underline"
                          >
                            View current SOA
                          </a>
                        )}
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">
                  {ewtAmount > 0 ? "Net Check Amount" : "Amount"}
                </label>
                <input
                  value={`₱${money(printableAmount)}`}
                  readOnly
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 bg-gray-50 text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-semibold text-gray-700">Input Tax Deduction</label>
                <div className="mt-1 flex flex-wrap items-center gap-3">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={withInputTax}
                      onChange={(e) => {
                        if (hasIncludedInputTax) return;
                        setWithInputTax(e.target.checked);
                      }}
                      disabled={hasIncludedInputTax}
                      className="w-4 h-4"
                    />
                    Calculate 12% automatically
                  </label>
                  {withInputTax && (
                    <span className="text-sm text-green-600 font-medium">₱{money(inputTaxAmount)}</span>
                  )}
                  {!withInputTax && (
                    <button
                      type="button"
                      onClick={addInputTaxRow}
                      className="rounded-full bg-green-50 px-4 py-2 text-xs font-semibold text-green-700 transition hover:bg-green-100"
                    >
                      + Add Input Tax
                    </button>
                  )}
                </div>
                {hasIncludedInputTax && (
                  <p className="mt-1 text-xs text-amber-600">
                    The selected request already has the input tax included. You can still add a manual deduction below if needed.
                  </p>
                )}
                  {withInputTax ? (
                  <p className="mt-1 text-xs text-green-600">
                    Input tax is calculated automatically at 12% of the request total and deducted from the amount.
                  </p>
                ) : (
                  <div className="mt-3 space-y-2 rounded-2xl border border-dashed border-green-200 bg-green-50/50 p-3">
                    {inputTaxRows.map((row, index) => (
                      <div key={row.id || index} className="rounded-2xl border border-green-100 bg-white p-4 shadow-sm">
                        <div className="space-y-4">
                          <div>
                            <div className="text-base font-semibold text-green-700">
                              Input Tax {index + 1}
                            </div>
                            <div className="mt-1 text-xs text-gray-500">
                              Enter the amount manually for this deduction.
                            </div>
                          </div>
                          {inputTaxPurposes.length > 0 && (
                            <div>
                              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                                Saved purpose
                              </label>
                              <select
                                value={inputTaxPurposes.includes(row.other_info || "") ? row.other_info : ""}
                                onChange={(e) => updateInputTaxRow(index, "other_info", e.target.value)}
                                className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-3 text-sm bg-white"
                              >
                                <option value="">Select saved purpose</option>
                                {inputTaxPurposes.map((purpose) => (
                                  <option key={purpose} value={purpose}>
                                    {purpose}
                                  </option>
                                ))}
                              </select>
                              <p className="mt-1 text-xs text-gray-500">
                                Choose a saved purpose or type a custom one below.
                              </p>
                            </div>
                          )}
                          <div>
                            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Other info
                            </label>
                            <input
                              type="text"
                              value={row.other_info || ""}
                              onChange={(e) => updateInputTaxRow(index, "other_info", e.target.value)}
                              className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-3 text-sm"
                                placeholder="e.g. VAT from shipment charges"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Amount
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={row.amount ?? ""}
                              onChange={(e) => updateInputTaxRow(index, "amount", e.target.value)}
                              className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-3 text-sm tabular-nums"
                              placeholder="0.00"
                            />
                          </div>
                          <div className="flex justify-end">
                            <button
                              type="button"
                              onClick={() => removeInputTaxRow(index)}
                              className="rounded-full border border-red-200 px-4 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                              disabled={inputTaxRows.length <= 1}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="flex items-center justify-between pt-1">
                      <span className="text-xs font-medium text-green-700">
                        Manual Input Tax Deduction Total
                      </span>
                      <span className="text-sm font-semibold text-green-700">
                        ₱{money(manualInputTaxTotal)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">EWT (Withholding Tax)</label>
                <select
                  value={ewtType}
                  disabled={hasIncludedEwt}
                  onChange={(e) => {
                    if (hasIncludedEwt) return;
                    const nextType = e.target.value;
                    setEwtType(nextType);
                    if (!nextType) {
                      setEwtAmountInput("");
                    } else if (nextType === "specific_amount") {
                      setEwtAmountInput("");
                    } else {
                      const nextOption = EWT_OPTIONS.find((option) => option.value === nextType);
                      const nextAmount = Number(
                        ((Number(selectedTotal || 0) * Number(nextOption?.rate || 0)) / 100).toFixed(2)
                      );
                      setEwtAmountInput(String(nextAmount || 0));
                    }
                  }}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white disabled:cursor-not-allowed disabled:bg-gray-100"
                >
                  {EWT_OPTIONS.map((option) => (
                    <option key={option.value || "none"} value={option.value}>
                      {option.label}
                      {option.rate ? ` (${option.rate}%)` : ""}
                    </option>
                  ))}
                </select>
                {hasIncludedEwt && (
                  <p className="mt-1 text-xs text-amber-600">
                    Includes ₱{money(particularsEwtTotal)} EWT from request particulars ({particularsEwtLabel}). Manual EWT is disabled to avoid double-withholding.
                  </p>
                )}
                {ewtType && (
                  <div className="mt-2 space-y-2">
                    {ewtType === "specific_amount" ? (
                      <div>
                        <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                          EWT Amount
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={ewtAmountInput}
                          onChange={(e) => setEwtAmountInput(e.target.value)}
                          className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                          placeholder="0.00"
                        />
                      </div>
                    ) : null}
                    <div className="text-sm text-amber-600 font-medium">
                      Withheld: ₱{money(ewtAmount)}
                      {withInputTax && (
                        <span className="ml-1 text-xs text-amber-500">
                          on gross base ₱{money(ewtBaseAmount)}
                        </span>
                      )}
                    </div>
                    {ewtType === "specific_amount" ? (
                      <p className="text-xs text-gray-500">
                        Enter the exact EWT amount manually.
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500">
                        Suggested by rate: ₱{money(computedEwtAmount)}
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-semibold text-gray-700">Amount in Words</label>
                <textarea
                  value={amountWords}
                  readOnly
                  className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm min-h-[72px] bg-gray-50"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">Preparer</label>
                <input
                  value={preparerName}
                  onChange={(e) => setPreparerName(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  placeholder="Prepared by"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700">Assigned checker</label>
                <select
                  value={checkedByUserId}
                  onChange={(e) => setCheckedByUserId(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white"
                >
                  <option value="">Select checker</option>
                  {checkedByUsers.map((u) => (
                    <option key={u.user_id} value={u.user_id}>
                      {getDisplayName(u)}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Assign the reviewer here. The actual check/approval happens on the checker side.
                </p>
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-semibold text-gray-700">Approved by</label>
                <input
                  value={approvedByName}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  readOnly
                />
                <p className="mt-2 text-xs text-gray-500">
                  Checker is assigned here. Actual check is recorded on the checker side.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white/95 border border-gray-100 rounded-3xl shadow-sm p-5">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div>
                <h2 className="text-lg font-semibold text-blue">Generate Accounting Entries</h2>
                <p className="text-xs text-gray-500">
                  Add one or more account titles on the debit side. Cash/bank remains automatic.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs uppercase tracking-[0.12em] text-gray-500">
                  {ewtType ? selectedEwtOption.label : "No tax"}
                </span>
                <button
                  type="button"
                  onClick={addAccountTitleRow}
                  className="rounded-full bg-blue-50 px-4 py-2 text-xs font-semibold text-blue-700 transition hover:bg-blue-100"
                >
                  + Add Account Title
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-700">
                  <tr className="text-left uppercase text-[11px] tracking-wide text-gray-500">
                    <th className="px-3 py-2">Account Title</th>
                    <th className="px-3 py-2 text-right">Debit</th>
                    <th className="px-3 py-2 text-right">Credit</th>
                    <th className="px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {generated.entries.map((line, index) => {
                    const isTaxLine = taxRoles.includes(line.role);
                    const isAccountTitleRow = line.role === "account_title";
                    const isCashRow = line.role === "cash";
                    const selectedAccount = accounts.find((account) => String(account.id) === String(line.account_id)) || null;
                    const isAdvancesAccount = isAdvancesToEmployeeAccountName(selectedAccount?.account_name || line.account_name || "");
                    const accountName = isTaxLine
                      ? line.account_name
                      : accounts.find((account) => String(account.id) === String(line.account_id))?.account_name || "";

                    const rowBgClass = line.role === "particulars_tax"
                      ? "bg-blue-50"
                      : line.role === "input_tax"
                        ? "bg-green-50"
                      : line.role === "ewt"
                          ? "bg-amber-50"
                          : line.role === "cash"
                            ? "bg-slate-50"
                            : "";

                    return (
                      <tr key={line.id || line.role || index} className={rowBgClass}>
                        <td className="px-3 py-2 align-top">
                          {isAccountTitleRow ? (
                            <div className="space-y-3">
                              <SearchableAccountSelect
                                value={line.account_id}
                                onChange={(nextValue) => updateAccountTitleAccount(index, nextValue)}
                                accounts={accounts}
                                placeholder="Search account"
                                className="w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                              />
                              {isAdvancesAccount && (
                                <div className="space-y-1">
                                  <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    Employee
                                  </label>
                                  <SearchableStringSelect
                                    value={
                                      line.particulars || ""
                                    }
                                    onChange={(value) => updateAccountTitleRow(index, "particulars", value)}
                                    options={employees.map((employee) => employee.display_name)}
                                    placeholder="Search employee"
                                    emptyLabel="No employees found."
                                    className="w-full rounded-lg border border-gray-200 px-2 py-2 text-sm bg-white"
                                  />
                                </div>
                              )}
                            </div>
                          ) : isCashRow ? (
                            <SearchableAccountSelect
                              value={cashAccountId}
                              onChange={setCashAccountId}
                              accounts={accounts}
                              placeholder="Search cash/bank account"
                              className="w-full rounded-lg border border-gray-200 px-2 py-2 text-sm"
                            />
                          ) : (
                            <div className="font-medium text-gray-800">
                              {accountName}
                              {line.role === "particulars_tax" && (
                                <span className="ml-2 text-xs text-blue-600">(auto from requests)</span>
                              )}
                              {line.role === "ewt" && (
                                <span className="ml-2 text-xs text-amber-600">(withheld)</span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right align-top">
                          {isAccountTitleRow ? (
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={line.amount ?? ""}
                              onChange={(e) => updateAccountTitleRow(index, "amount", e.target.value)}
                              className="w-full rounded-lg border border-gray-200 px-2 py-2 text-right text-sm tabular-nums"
                              placeholder="0.00"
                            />
                          ) : line.debit ? (
                            `₱${money(line.debit)}`
                          ) : (
                            ""
                          )}
                        </td>
                        <td className="px-3 py-2 text-right align-top">
                          {line.credit ? `₱${money(line.credit)}` : ""}
                        </td>
                        <td className="px-3 py-2 text-right align-top">
                          {isAccountTitleRow ? (
                            <button
                              type="button"
                              onClick={() => removeAccountTitleRow(index)}
                              className="rounded-full border border-red-200 px-3 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                              disabled={accountTitleRows.length <= 1}
                            >
                              Remove
                            </button>
                          ) : isCashRow ? (
                            <span className="text-xs uppercase tracking-wide text-gray-400">Auto</span>
                          ) : (
                            <span className="text-xs uppercase tracking-wide text-gray-400">Auto</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-3 py-2 text-right">Totals</td>
                    <td className="px-3 py-2 text-right">₱{money(lineTotals.debit)}</td>
                    <td className="px-3 py-2 text-right">₱{money(lineTotals.credit)}</td>
                    <td className="px-3 py-2 text-right" />
                  </tr>
                  {particularsTaxTotal > 0 && (
                    <tr className="bg-blue-50">
                      <td className="px-3 py-2 text-right text-blue-700" colSpan={4}>
                        Includes ₱{money(particularsTaxTotal)} Input Tax from request particulars
                      </td>
                    </tr>
                  )}
                  <tr className="bg-gray-50">
                    <td className="px-3 py-2 text-right" colSpan={3}>
                      {withInputTax && ewtType ? "Voucher amount with input tax shown separately, net of EWT" :
                       withInputTax ? "Voucher amount with input tax shown separately" :
                       ewtType ? "Voucher amount net of EWT" : "Voucher amount"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {Math.abs(lineTotals.debit - lineTotals.credit) < 0.01 ? (
                        <span className="text-green-600">Balanced</span>
                      ) : (
                        <span className="text-red-600">Unbalanced</span>
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div className="bg-white/95 border border-gray-100 rounded-3xl shadow-sm p-5">
            <h2 className="text-lg font-semibold text-blue mb-3">Selected Requests</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-700">
                  <tr className="text-left uppercase text-[11px] tracking-wide text-gray-500">
                    <th className="px-3 py-2">RFP No</th>
                    <th className="px-3 py-2">Payee</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {requests.map((request) => (
                    <tr key={request.id}>
                      <td className="px-3 py-2 font-semibold">{request.rfp_no || "—"}</td>
                      <td className="px-3 py-2">{request.payee || "—"}</td>
                      <td className="px-3 py-2 text-right">₱{money(request.total_amount || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="bg-white/95 border border-gray-100 rounded-3xl shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-blue">Preview</h2>
            <span className="text-xs uppercase tracking-[0.12em] text-gray-500">Printable layout</span>
          </div>
          <CheckVoucherPreview
            voucherNo={voucherNo || "CV-XX-XX-XXXX"}
            voucherDate={voucherDate}
            payee={selectedPayee}
            checkNo={checkNo}
            checkDate={checkDate}
            details={[
              {
                label: "Input Tax",
                value: withInputTax
                  ? `VAT 12% (₱${money(inputTaxAmount)})`
                  : particularsTaxTotal > 0
                    ? `From request particulars (₱${money(particularsTaxTotal)})`
                    : "—",
              },
              {
                label: "For SOA",
                value: forSoa ? `Yes${soaRemarks.trim() ? ` — ${soaRemarks.trim()}` : ""}` : "No",
              },
              {
                label: "Withheld",
                value: ewtType
                  ? withInputTax
                    ? `₱${money(ewtAmount)} on gross base ₱${money(ewtBaseAmount)}`
                    : `${selectedEwtOption.label} (₱${money(ewtAmount)})`
                  : generated.particularsEwtAmount > 0
                    ? `From request particulars (₱${money(generated.particularsEwtAmount)})`
                    : "—",
              },
            ]}
            amountLabel={ewtAmount > 0 || generated.particularsEwtAmount > 0 ? "Net Check Amount" : "Amount"}
            amount={printableAmount}
            amountWords={amountWords}
            preparerName={preparerName}
            assignedCheckerName={checkedByDisplayName || "Pending"}
            checkedByName={savedVoucher?.checked_by_name || "Pending"}
            approvedByName={approvedByName || "—"}
            receivedByName={savedVoucher?.received_by_name || "Pending"}
            preparerSignatureUrl={preparerSignatureUrl}
            checkedBySignatureUrl={savedVoucher?.checked_by_signature_url || null}
            lines={generated.entries.map((line) => ({
              id: line.id,
              account_name: line.account_name,
              particulars: line.particulars,
              debit: line.debit,
              credit: line.credit,
            }))}
            requests={requests}
            soaFileName={forSoa ? soaFile?.name || savedVoucher?.soa_file_name || "" : ""}
            soaFileUrl={forSoa ? savedVoucher?.soa_file_url || "" : ""}
          />
        </div>
      </div>
    </div>
  );
};

export default CheckVoucherCreate;
